import { isHtmlEffectivelyEmpty } from '@kula-ai/components';

export type EmailRequiredField = 'from' | 'subject' | 'body';

/** Copy matches the toasts `EmailTemplates` has always shown. */
export const EMAIL_REQUIRED_MESSAGES: Record<EmailRequiredField, string> = {
  from: 'From is mandatory',
  subject: 'Subject is mandatory',
  body: 'Body is mandatory',
};

interface EmailComposition {
  from?: unknown;
  subject?: unknown;
  body?: unknown;
}

interface ValidateOptions {
  requireSubject?: boolean;
  requireBody?: boolean;
}

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * True when the editor holds nothing meaningful.
 *
 * tiptap serialises an empty document as `<p></p>`, which is truthy — so a
 * plain `!body` check lets an empty body through to the backend.
 */
export const isEmailBodyEmpty = (body: unknown): boolean =>
  isBlank(body) || isHtmlEffectivelyEmpty(String(body ?? ''));

/**
 * First missing required field, in the order the toasts report them.
 *
 * kula `Email` renders inline errors off `submitAttempt` but never blocks a
 * submit — that has always been the surface's job. Several surfaces had no
 * check at all, so an unprefilled From sent with no sender.
 */
export const validateEmailComposition = (
  values: EmailComposition | undefined | null,
  { requireSubject = true, requireBody = true }: ValidateOptions = {}
): { field: EmailRequiredField; message: string } | null => {
  if (isBlank(values?.from)) {
    return { field: 'from', message: EMAIL_REQUIRED_MESSAGES.from };
  }
  if (requireSubject && isBlank(values?.subject)) {
    return { field: 'subject', message: EMAIL_REQUIRED_MESSAGES.subject };
  }
  if (requireBody && isEmailBodyEmpty(values?.body)) {
    return { field: 'body', message: EMAIL_REQUIRED_MESSAGES.body };
  }
  return null;
};

/** The merge-variable payload keyed by category. */
export type EmailVariables = Record<string, string[]> | undefined | null;

/**
 * Flattens the category-keyed variables into the flat allowlist the
 * invalid-variable checks take. Every email surface derived this the same way.
 */
export const toVariablesList = (variables: EmailVariables): string[] =>
  Object.entries(variables || {}).flatMap(([, values = []]) => values);
